import React from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import 'react-calendar/dist/Calendar.css';
import '../../../styles/date-ranger-picker.css';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import CompanyDropdown from '../../../components/CompanyDropdown';
import { crmService } from '../../../services/central-api/crm';
import { useForm } from 'react-hook-form';
import FormSwitch from '../../../components/FormSwitch';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
}

interface DownloadForm {
    companyId: number;
    offlineOnly: boolean;
    disabledOnly: boolean;
    withThirdParty: boolean;
    notIncludePanoramicChildren: boolean;
    wrongHttpAddressOnly: boolean;
}

export default function DownloadReportModal({ isModalVisible, setModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const defaultEmptyValues: DownloadForm = {
        companyId: 0,
        offlineOnly: false,
        disabledOnly: false,
        withThirdParty: false,
        notIncludePanoramicChildren: false,
        wrongHttpAddressOnly: false
    };

    const form = useForm<DownloadForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    function onCloseModal() {
        form.reset(defaultEmptyValues);
        setModalVisible(false);
    }

    async function formSubmit() {
        try {
            const csv = await crmService.downloadCameraReport(form.getValues());
            if (!(csv instanceof Blob)) {
                throw new Error();
            }
            const url = window.URL.createObjectURL(csv);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cameras-${moment(new Date()).format('YYYY-MM-DD')}.csv`;
            a.click();
            window.URL.revokeObjectURL(url);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            onCloseModal();
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function isFormValid() {
        return form.watch('companyId') !== 0;
    }

    return (
        <Modal transparent={true} visible={isModalVisible} animationType='fade' onRequestClose={() => onCloseModal()} >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 400 }]}>
                    <CompanyDropdown
                        form={form}
                        fieldName='companyId'
                        required={true}
                        disabled={false}
                        zIndex={2}
                    />
                    <FormSwitch
                        label={translate('offlineOnly')}
                        form={form}
                        fieldName='offlineOnly'
                    />
                    <FormSwitch
                        label={translate('disabledOnly')}
                        form={form}
                        fieldName='disabledOnly'
                    />
                    <FormSwitch
                        label={translate('withThirdParty')}
                        form={form}
                        fieldName='withThirdParty'
                    />
                    <FormSwitch
                        label={translate('wrongHttpAddressOnly')}
                        form={form}
                        fieldName='wrongHttpAddressOnly'
                    />
                    <FormSwitch
                        label={translate('notIncludePanoramicChildren')}
                        form={form}
                        fieldName='notIncludePanoramicChildren'
                    />
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => onCloseModal()}
                        disabled={!isFormValid()}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 15,
        maxHeight: 690
    }
}));
